import { useEffect, useRef, useState } from 'react';

import { cn } from '@april9au/react-common/utils';

import { SectionHeader } from './section-header';

export type MarketStat = {
  value: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  label: string;
  caption: string;
};

function formatStatValue(value: number, decimals = 0, prefix = '', suffix = '') {
  const formatted = value.toLocaleString('en-AU', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Counts up from 0 to `stat.value` once `active` turns true, then calls
 * `onDone` — the parent uses that to run the row one stat at a time rather
 * than all of them at once. Renders the final value up front (so it's there
 * for no-JS/SEO) until it's this stat's turn.
 */
function AnimatedStat({ stat, active, onDone }: { stat: MarketStat; active: boolean; onDone: () => void }) {
  const finalDisplay = formatStatValue(stat.value, stat.decimals, stat.prefix, stat.suffix);
  const [display, setDisplay] = useState(finalDisplay);

  // `onDone` is a fresh closure on every parent render — read it through a ref
  // instead of depending on it directly, so an unrelated parent re-render
  // mid-animation can't tear down this effect (cancelling the in-flight
  // rAF loop) and leave the counter stuck partway.
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!active) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onDoneRef.current();
      return;
    }

    setDisplay(formatStatValue(0, stat.decimals, stat.prefix, stat.suffix));

    let frame = 0;
    const duration = 900;
    const start = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(formatStatValue(stat.value * eased, stat.decimals, stat.prefix, stat.suffix));
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        onDoneRef.current();
      }
    };
    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [active, stat.value, stat.decimals, stat.prefix, stat.suffix]);

  return <span className="text-5xl font-light text-gold tabular-nums">{display}</span>;
}

/**
 * "By the numbers" band — hard-coded Kingaroy/South Burnett market figures
 * (sourced from realestate.com.au-derived suburb data and ABS/council
 * population releases) that count up into view one at a time, in order. Sits
 * right under the hero, ahead of any on-page proof, so update the `stats`
 * values in routes/_index.tsx whenever fresher figures are published.
 */
export function MarketStats({
  id,
  eyebrow,
  heading,
  description,
  stats,
  sourceNote,
  className,
}: {
  id?: string;
  eyebrow?: string;
  heading: string;
  description?: string;
  stats: Array<MarketStat>;
  sourceNote?: string;
  className?: string;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const node = rowRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        observer.disconnect();
        setActiveIndex(0);
      },
      { threshold: 0.4 },
    );
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  return (
    <section id={id} className={cn('section--navy-mid py-14 md:py-20', className)}>
      <div className="container">
        <SectionHeader eyebrow={eyebrow} heading={heading} description={description} align="left" className="mb-10" />

        <div ref={rowRef} className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-3">
          {stats.map((stat, index) => (
            <div key={stat.label} className={cn('flex flex-col gap-2', index > 0 && 'sm:border-l sm:border-white/10 sm:pl-8')}>
              <AnimatedStat
                stat={stat}
                active={activeIndex === index}
                onDone={() => setActiveIndex((current) => (current === index ? index + 1 : current))}
              />
              <span className="text-eyebrow">{stat.label}</span>
              <p className="text-body text-sm">{stat.caption}</p>
            </div>
          ))}
        </div>

        {sourceNote ? <p className="text-caption mt-10">{sourceNote}</p> : null}
      </div>
    </section>
  );
}
